import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  InputAdornment,
  TextField,
  Button,
  Paper,
  Box,
  Icon,
  Alert
} from "@mui/material";
import ApiList from "../../partials/ApiList";
import ResultField from "../../partials/ResultField";
import { SIG_FIGS, DISCLAIMER, DISCLAIMER_CREAM, UNITS } from "../../../resources/constants";
import ingredients,{TYPES} from '../../../resources/ingredients';
import { numbersOnly } from "../../../utilities/strings";
import validate from "./validate";
import calculate from "./calculate";
import { TextFieldRightAligned } from '../../../utilities/theme';
import AutoCompleteTextField from "../../partials/AutoCompleteTextField";

//calulates the bases to show
const solvents = ingredients
  .filter((x) => x.type === TYPES.solvent)
  .map((x) => ({ ...x, label: x.name, value: x.name }));



/**
 * CreamTab
 * @param {*} props
 * @returns
 */
export default function BiestTab() {
  const [totalQuantity, setTotalQuantity] = useState('');
  const [solvent,setSolvent] = useState(null);
  const [solventPercent,setSolventPercent] = useState('');
  const [biestQuantity,setBiestQuantity] = useState('');
  const [biestRatio, setBiestRatio] = useState('');
  

  const [apiList, setApiList] = useState([{}]);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});

  //useEffect: reset results if any variables change
  useEffect((_) => {
    setResults(null);
  }, [totalQuantity,solvent,solventPercent,biestQuantity,biestRatio,apiList]);


  const getState = (_) => ({
    totalQuantity,solvent,solventPercent,biestQuantity,biestRatio,apiList
  });

  //on button click
  const onCalculate = (_) => {
    const state = getState();
    if (validate(setErrors, state)) {
      calculate(setResults, state);
    }
  };

  const renderTitle = (
    <Grid container spacing={1} alignItems='center'>
      <Grid item md={12} py={2}>
        <Typography variant="h4">Hormone Cream Calculator with Bi-est Component</Typography>
        <Typography variant="body1">
          Please supply the information below...
        </Typography>
      </Grid>
    </Grid>
  );

  const renderDisclaimer = (
    <Grid container spacing={1}>
      <Grid item xs={12} my={2}>
        <Alert severity="warning">{DISCLAIMER}</Alert>
      </Grid>
    </Grid>
  );

  const renderInputs = (
    <Grid container spacing={1}>
      <Grid item md={4}>
        <TextField
          my={1}
          required
          label="Total Quantity Desired"
          value={totalQuantity}
          onChange={(e) => setTotalQuantity(numbersOnly(e.target.value, true))}
          error={errors.totalQuantity}
          helperText={errors.totalQuantity}
          fullWidth
          {...TextFieldRightAligned}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography>g</Typography>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item md={4}>
      <AutoCompleteTextField
          required
          my={1}
          name="solvent"
          label="Select a Solvent *"
          value={solvent}
          data={solvents}
          onChange={(_, v) => setSolvent(v)}
          error={errors.solvent}
        />
      </Grid>
      <Grid item md={4}>
        <TextField
          my={1}
          required
          label="Solvent Percent"
          value={solventPercent}
          onChange={(e) => setSolventPercent(numbersOnly(e.target.value, true))}
          error={errors.solventPercent}
          helperText={errors.solventPercent}
          fullWidth
          {...TextFieldRightAligned}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography>%</Typography>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item md={4}>
        <TextField
          my={1}
          required
          label="Biest Quantity"
          value={biestQuantity}
          onChange={(e) => setBiestQuantity(numbersOnly(e.target.value, true))}
          error={errors.biestQuantity}
          helperText={errors.biestQuantity}
          fullWidth
          {...TextFieldRightAligned}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography>mg/g</Typography>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item md={4}>
        <TextField
          my={1}
          required
          label="Biest Ratio (Estriol:Estradiol)"
          value={biestRatio}
          onChange={(e) => setBiestRatio(numbersOnly(e.target.value, true))}
          error={errors.biestRatio}
          helperText={errors.biestRatio}
          fullWidth
          {...TextFieldRightAligned}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" style={{width:'50%'}}>
                <Typography>: {100-biestRatio}</Typography>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      {solvent !== null &&
        <Grid item md={12}>
          <Alert severity="info">{DISCLAIMER_CREAM}</Alert>
        </Grid>
      }
    </Grid>
  );

  const renderApiList = (
    <Grid container spacing={1} py={1}>
      <Grid item xs>
        <ApiList
          data={apiList}
          errors={errors.apiList}
          results={results?.apiList}
          onChange={setApiList}
          unit={UNITS.mgPerGram}
          countRange = {{ min: 0, max: 6 }}
          numberField={{
            label:'Number of Additional APIs'
          }}
        />
      </Grid>
    </Grid>
  );

  const renderButton = (
    <Grid container spacing={1} py={1}>
      <Grid item xs>
        <Button
          variant="contained"
          color="primary"
          endIcon={<Icon className="fa fa-paper-plane" />}
          onClick={onCalculate}
        >
          Calculate
        </Button>
      </Grid>
    </Grid>
  );

  const renderResults = results && (
    <Grid container spacing={1}>
      <Grid item xs my={2}>
        <Paper variant="secondary">
          <Box p={2}>
            <Typography variant="h5">Results</Typography>
            <ResultField label="Quantity of Estriol">
              {results.estriolQuantity.toFixed(SIG_FIGS)} g
            </ResultField>
            <ResultField label="Quantity of Estradiol">
              {results.estradiolQuantity.toFixed(SIG_FIGS)} g
            </ResultField>
            <ResultField label="Quantity of Solvent">
              {results.solventQuantity.toFixed(SIG_FIGS)} g<br/>
              {results.solventQuantityVolume.toFixed(SIG_FIGS)} mL
            </ResultField>
            <ResultField label="Vehicle Quantity">
              {results.vehicle.toFixed(SIG_FIGS)} g
            </ResultField>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );

  return (
    <Grid container spacing={1}>
      {renderTitle}
      {renderInputs}
      {renderApiList}
      {renderButton}
      {renderResults}
      {renderDisclaimer}
    </Grid>
  );
}
